import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, GripVertical, Check, X, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const QuizEditor = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    tipo: 'multiple_choice',
    texto: '',
    explicacion: '',
    opciones: [{ id: crypto.randomUUID(), texto: '', is_correct: false }],
    respuesta_correcta: '',
    puntos: 1,
    orden: 0
  });

  useEffect(() => {
    fetchQuiz();
  }, [quizId]);

  const fetchQuiz = async () => {
    try {
      const response = await axios.get(`${API_URL}/quizzes/${quizId}`);
      setQuiz(response.data);
    } catch (error) {
      toast.error('Error al cargar el quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuestion = async () => {
    if (!questionForm.texto.trim()) {
      toast.error('El texto de la pregunta es obligatorio');
      return;
    }

    // Validar según tipo
    if (['multiple_choice', 'multiple_answer'].includes(questionForm.tipo)) {
      if (questionForm.opciones.length < 2) {
        toast.error('Debe haber al menos 2 opciones');
        return;
      }
      const hasCorrect = questionForm.opciones.some(o => o.is_correct);
      if (!hasCorrect) {
        toast.error('Debe marcar al menos una respuesta correcta');
        return;
      }
    }

    if (['true_false', 'fill_blank'].includes(questionForm.tipo)) {
      if (!questionForm.respuesta_correcta) {
        toast.error('Debe indicar la respuesta correcta');
        return;
      }
    }

    setSaving(true);
    try {
      const data = {
        ...questionForm,
        quiz_id: quizId,
        orden: editingQuestion ? editingQuestion.orden : (quiz.questions?.length || 0) + 1
      };

      if (editingQuestion) {
        await axios.put(`${API_URL}/questions/${editingQuestion.id}`, data);
        toast.success('Pregunta actualizada');
      } else {
        await axios.post(`${API_URL}/questions`, data);
        toast.success('Pregunta creada');
      }

      setShowQuestionDialog(false);
      resetQuestionForm();
      fetchQuiz();
    } catch (error) {
      toast.error('Error al guardar la pregunta');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('¿Eliminar esta pregunta?')) return;
    try {
      await axios.delete(`${API_URL}/questions/${questionId}`);
      toast.success('Pregunta eliminada');
      fetchQuiz();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      tipo: question.tipo,
      texto: question.texto,
      explicacion: question.explicacion || '',
      opciones: question.opciones || [{ id: crypto.randomUUID(), texto: '', is_correct: false }],
      respuesta_correcta: question.respuesta_correcta || '',
      puntos: question.puntos || 1,
      orden: question.orden || 0
    });
    setShowQuestionDialog(true);
  };

  const resetQuestionForm = () => {
    setEditingQuestion(null);
    setQuestionForm({
      tipo: 'multiple_choice',
      texto: '',
      explicacion: '',
      opciones: [{ id: crypto.randomUUID(), texto: '', is_correct: false }],
      respuesta_correcta: '',
      puntos: 1,
      orden: 0
    });
  };

  const addOption = () => {
    setQuestionForm(prev => ({
      ...prev,
      opciones: [...prev.opciones, { id: crypto.randomUUID(), texto: '', is_correct: false }]
    }));
  };

  const removeOption = (index) => {
    setQuestionForm(prev => ({
      ...prev,
      opciones: prev.opciones.filter((_, i) => i !== index)
    }));
  };

  const updateOption = (index, field, value) => {
    setQuestionForm(prev => {
      const newOpciones = [...prev.opciones];
      newOpciones[index] = { ...newOpciones[index], [field]: value };
      
      // Si es multiple_choice, desmarcar las demás al marcar una
      if (field === 'is_correct' && value && prev.tipo === 'multiple_choice') {
        newOpciones.forEach((opt, i) => {
          if (i !== index) opt.is_correct = false;
        });
      }
      
      return { ...prev, opciones: newOpciones };
    });
  };

  const getQuestionTypeLabel = (tipo) => {
    const labels = {
      multiple_choice: 'Opción múltiple (una respuesta)',
      multiple_answer: 'Opción múltiple (varias respuestas)',
      true_false: 'Verdadero / Falso',
      fill_blank: 'Rellenar hueco',
      short_answer: 'Respuesta corta (manual)'
    };
    return labels[tipo] || tipo;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver
            </Button>
            <div>
              <h1 className="font-manrope font-bold text-2xl text-slate-900">{quiz?.titulo}</h1>
              <p className="text-slate-500 text-sm">{quiz?.descripcion}</p>
            </div>
          </div>
          <Button onClick={() => { resetQuestionForm(); setShowQuestionDialog(true); }} className="bg-[#004E89] hover:bg-[#003B66]">
            <Plus className="w-4 h-4 mr-2" /> Nueva Pregunta
          </Button>
        </div>

        {/* Quiz Settings Summary */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Nota mínima:</span>
              <span className="ml-2 font-medium">{quiz?.nota_minima}%</span>
            </div>
            <div>
              <span className="text-slate-500">Tiempo límite:</span>
              <span className="ml-2 font-medium">{quiz?.tiempo_limite ? `${quiz.tiempo_limite} min` : 'Sin límite'}</span>
            </div>
            <div>
              <span className="text-slate-500">Intentos:</span>
              <span className="ml-2 font-medium">{quiz?.intentos_permitidos || 'Ilimitados'}</span>
            </div>
            <div>
              <span className="text-slate-500">Preguntas:</span>
              <span className="ml-2 font-medium">{quiz?.questions?.length || 0}</span>
            </div>
          </div>
        </div>

        {/* Questions List */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-semibold text-lg">Preguntas</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {(quiz?.questions || []).map((question, index) => (
              <div key={question.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#004E89] text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{question.texto}</p>
                        <p className="text-sm text-slate-500 mt-1">
                          {getQuestionTypeLabel(question.tipo)} • {question.puntos} punto{question.puntos !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openEditQuestion(question)}>
                          Editar
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteQuestion(question.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Preview options */}
                    {['multiple_choice', 'multiple_answer'].includes(question.tipo) && question.opciones && (
                      <div className="mt-3 space-y-1">
                        {question.opciones.map((opt, i) => (
                          <div key={opt.id || i} className={`flex items-center gap-2 text-sm ${opt.is_correct ? 'text-green-700' : 'text-slate-600'}`}>
                            {opt.is_correct ? <Check className="w-4 h-4" /> : <span className="w-4" />}
                            {opt.texto}
                          </div>
                        ))}
                      </div>
                    )}
                    {question.tipo === 'true_false' && (
                      <p className="mt-2 text-sm text-green-700">Respuesta: {question.respuesta_correcta}</p>
                    )}
                    {question.tipo === 'fill_blank' && (
                      <p className="mt-2 text-sm text-green-700">Respuesta: {question.respuesta_correcta}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {(!quiz?.questions || quiz.questions.length === 0) && (
              <div className="p-12 text-center">
                <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No hay preguntas en este quiz</p>
                <p className="text-sm text-slate-400 mt-1">Añade la primera pregunta para comenzar</p>
              </div>
            )}
          </div>
        </div>

        {/* Question Dialog */}
        <Dialog open={showQuestionDialog} onOpenChange={(open) => { setShowQuestionDialog(open); if (!open) resetQuestionForm(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingQuestion ? 'Editar Pregunta' : 'Nueva Pregunta'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Tipo de pregunta</Label>
                <Select value={questionForm.tipo} onValueChange={(v) => setQuestionForm(prev => ({ ...prev, tipo: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multiple_choice">Opción múltiple (una respuesta)</SelectItem>
                    <SelectItem value="multiple_answer">Opción múltiple (varias respuestas)</SelectItem>
                    <SelectItem value="true_false">Verdadero / Falso</SelectItem>
                    <SelectItem value="fill_blank">Rellenar hueco</SelectItem>
                    <SelectItem value="short_answer">Respuesta corta (evaluación manual)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Texto de la pregunta *</Label>
                <Textarea
                  value={questionForm.texto}
                  onChange={(e) => setQuestionForm(prev => ({ ...prev, texto: e.target.value }))}
                  placeholder="Escribe la pregunta..."
                  rows={3}
                />
              </div>

              <div>
                <Label>Puntos</Label>
                <Input
                  type="number"
                  min="1"
                  value={questionForm.puntos}
                  onChange={(e) => setQuestionForm(prev => ({ ...prev, puntos: parseInt(e.target.value) || 1 }))}
                  className="w-24"
                />
              </div>

              {/* Options for multiple choice */}
              {['multiple_choice', 'multiple_answer'].includes(questionForm.tipo) && (
                <div>
                  <Label>Opciones de respuesta</Label>
                  <p className="text-sm text-slate-500 mb-2">
                    {questionForm.tipo === 'multiple_choice' ? 'Marca la respuesta correcta' : 'Marca todas las respuestas correctas'}
                  </p>
                  <div className="space-y-2">
                    {questionForm.opciones.map((opt, index) => (
                      <div key={opt.id} className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => updateOption(index, 'is_correct', !opt.is_correct)}
                          className={`w-6 h-6 rounded border flex items-center justify-center flex-shrink-0 ${
                            opt.is_correct ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300'
                          }`}
                        >
                          {opt.is_correct && <Check className="w-4 h-4" />}
                        </button>
                        <Input
                          value={opt.texto}
                          onChange={(e) => updateOption(index, 'texto', e.target.value)}
                          placeholder={`Opción ${index + 1}`}
                          className="flex-1"
                        />
                        {questionForm.opciones.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeOption(index)}>
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" onClick={addOption} className="mt-2">
                    <Plus className="w-4 h-4 mr-1" /> Añadir opción
                  </Button>
                </div>
              )}

              {/* True/False */}
              {questionForm.tipo === 'true_false' && (
                <div>
                  <Label>Respuesta correcta</Label>
                  <Select
                    value={questionForm.respuesta_correcta}
                    onValueChange={(v) => setQuestionForm(prev => ({ ...prev, respuesta_correcta: v }))}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Selecciona..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Verdadero">Verdadero</SelectItem>
                      <SelectItem value="Falso">Falso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Fill blank */}
              {questionForm.tipo === 'fill_blank' && (
                <div>
                  <Label>Respuesta correcta</Label>
                  <Input
                    value={questionForm.respuesta_correcta}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, respuesta_correcta: e.target.value }))}
                    placeholder="Escribe la respuesta correcta..."
                  />
                  <p className="text-sm text-slate-500 mt-1">La comparación no distingue mayúsculas/minúsculas</p>
                </div>
              )}

              {/* Short answer info */}
              {questionForm.tipo === 'short_answer' && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    Las preguntas de respuesta corta requieren evaluación manual por el instructor.
                  </p>
                </div>
              )}

              <div>
                <Label>Explicación (opcional)</Label>
                <Textarea
                  value={questionForm.explicacion}
                  onChange={(e) => setQuestionForm(prev => ({ ...prev, explicacion: e.target.value }))}
                  placeholder="Explicación de la respuesta correcta..."
                  rows={2}
                />
                <p className="text-sm text-slate-500 mt-1">Se muestra al alumno tras completar el quiz</p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowQuestionDialog(false)}>Cancelar</Button>
                <Button onClick={handleSaveQuestion} disabled={saving} className="bg-[#004E89] hover:bg-[#003B66]">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default QuizEditor;
